/**
 * Settings → Workspace — resource budget editor (Phase 12 / M10).
 *
 * Read access is open to any workspace member; writes are gated by
 * the gateway to founder + admin (PATCH /api/workspaces/me/budget).
 * The page renders the disabled state for read-only roles so the
 * intent stays discoverable.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { ApiError } from '../../../lib/api/client';
import { workspaceApi, workspaceKeys } from '../../../lib/api/workspace';
import { useAuthStore } from '../../../stores/auth-store';

const WRITE_ROLES = new Set(['founder', 'admin']);

function WorkspaceSettingsPage() {
  const qc = useQueryClient();
  const role = useAuthStore(
    (s) => s.workspaces.find((w) => w.workspace.id === s.activeWorkspaceId)?.membership.role,
  );
  const canWrite = role ? WRITE_ROLES.has(role) : false;

  const budgetQ = useQuery({
    queryKey: workspaceKeys.budget(),
    queryFn: () => workspaceApi.getBudget(),
  });

  const [cap, setCap] = React.useState<number>(10);
  const [autoPause, setAutoPause] = React.useState<boolean>(true);
  const [status, setStatus] = React.useState<{
    kind: 'idle' | 'saving' | 'saved' | 'error';
    message?: string;
  }>({ kind: 'idle' });

  React.useEffect(() => {
    if (budgetQ.data) {
      setCap(budgetQ.data.max_concurrent_active_sessions);
      setAutoPause(budgetQ.data.auto_pause_oldest_when_at_cap);
    }
  }, [budgetQ.data]);

  const mutation = useMutation({
    mutationFn: () =>
      workspaceApi.updateBudget({
        max_concurrent_active_sessions: cap,
        auto_pause_oldest_when_at_cap: autoPause,
      }),
    onMutate: () => setStatus({ kind: 'saving' }),
    onSuccess: (budget) => {
      qc.setQueryData(workspaceKeys.budget(), budget);
      setStatus({ kind: 'saved' });
      window.setTimeout(() => setStatus({ kind: 'idle' }), 1500);
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : String(e);
      setStatus({ kind: 'error', message: msg });
    },
  });

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Workspace</h1>
        <p className="text-sm text-muted-foreground">
          Capacity controls for AI sessions running in this workspace.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Resource budget</CardTitle>
          <CardDescription>
            Limit how many AI sessions can run at once. The gateway blocks new sessions when this
            cap is reached.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="budget-cap">Max concurrent active sessions (1–50)</Label>
            <Input
              id="budget-cap"
              type="number"
              min={1}
              max={50}
              value={cap}
              onChange={(e) => setCap(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
              disabled={!canWrite || budgetQ.isLoading}
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoPause}
              disabled={!canWrite || budgetQ.isLoading}
              onChange={(e) => setAutoPause(e.target.checked)}
              className="h-4 w-4 accent-foreground"
            />
            Auto-pause the oldest idle session when at cap
          </label>
          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              disabled={!canWrite || mutation.isPending}
              onClick={() => mutation.mutate()}
              className="inline-flex h-8 items-center rounded border border-border bg-foreground/[0.04] px-3 text-xs font-medium text-foreground transition-colors hover:bg-foreground/[0.08] disabled:opacity-50"
            >
              {mutation.isPending && <Loader2 size={11} className="mr-1 animate-spin" />}
              Save changes
            </button>
            {status.kind === 'saved' && (
              <span className="text-xs text-emerald-600 dark:text-emerald-400">Saved.</span>
            )}
            {status.kind === 'error' && (
              <span className="text-xs text-rose-600 dark:text-rose-400">{status.message}</span>
            )}
            {!canWrite && (
              <span className="text-xs text-muted-foreground">
                Read-only — founder or admin role required to edit.
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/settings/workspace')({
  component: WorkspaceSettingsPage,
});
